<template>
    <div>
        <div class="h5 mb-3">
            {{ resendESignaturesSubheader }}
        </div>
        <div
            v-if="!canResendAnyDocuments"
            class="mb-3">
            <span>
                {{ noDocumentsResendableMsg }}
            </span>
            <span>
                <pc-help-icon v-on:click="showResendHelp = !showResendHelp" />
            </span>
        </div>
        <div v-else>
            <div class="mb-3">
                {{ resendESignaturesInfoMsg }}
            </div>
            <div class="mb-3">
                {{ resendESignaturesPreviewLabel }}
            </div>
            <div
                v-for="(documentStatus, i) in documentsToResend"
                v-bind:key="`documentStatus-${i}`"
                class="mb-3">
                <div>
                    <div class="fw-bold">
                        {{ documentStatus.Name }}
                    </div>
                    <div
                        v-for="(signatory, j) in getResendableSignatories(documentStatus)"
                        v-bind:key="`signatory-${j}`"
                        class="ms-3">
                        <resend-contact-info-preview v-bind:signatory="signatory" />
                    </div>
                </div>
            </div>
            <pc-alert
                v-bind:shown="!allResendableSignatoriesHaveContactInfo"
                v-bind:message="missingContactInfoErrorMsg"
                v-bind:severity="alertSeverity.error" />
            <div
                v-if="resendEnabled"
                class="mb-3">
                <span class="fst-italic text-body-secondary">
                    {{ resendLimitationsNote }}
                </span>
                <span>
                    <pc-help-icon v-on:click="showResendHelp = !showResendHelp" />
                </span>
            </div>
        </div>
        <pc-alert
            v-model:shown="showResendHelp"
            dismissible>
            {{ resendLimitationsHelpMsg }}
        </pc-alert>
        <div class="d-flex justify-content-between">
            <pc-button
                link
                class="p-0"
                v-on:click="showCancelSection = !showCancelSection">
                {{ showCancelSection ? hideOptionsBtnLabel : otherOptionsBtnLabel }}
            </pc-button>
            <div>
                <pc-button
                    v-if="!hasResent && canResendAnyDocuments"
                    v-bind:disabled="!resendEnabled"
                    v-on:click="$emit('resend-notifications')">
                    {{ resendBtnLabel }}
                </pc-button>
                <div
                    v-else-if="hasResent"
                    class="fst-italic">
                    {{ notificationsResentMsg }}
                </div>
            </div>
        </div>
        <pc-transition transition="slide">
            <div v-if="showCancelSection">
                <hr class="mx-n3" />
                <div class="h5 mb-3">
                    {{ cancelESignaturesSubheader }}
                </div>
                <cancel-signatures-dialog
                    v-bind:show-cancel-incomplete="showCancelIncomplete"
                    v-bind:show-dont-cancel="false"
                    v-bind:cancel-explanation-options="cancelExplanationOptions"
                    show-cancel-deterrents
                    v-on:cancel-incomplete="$emit('cancel-incomplete', $event)"
                    v-on:cancel-all="$emit('cancel-all', $event)" />
            </div>
        </pc-transition>
    </div>
</template>
<script>
    import Vuex from "vuex";
    import { isModuleRegistered } from "SharedStores/util";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_NAMESPACE, PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { ALERT_SEVERITY } from "Util/alerts";

    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/cancelResendEnvelopeDialog.i18n";
    import { ESIGNATURE_CANCEL_EXPLANATION_TYPE } from "ESignatures/eSignatureConstants";
    import resendContactInfoPreview from "./resendContactInfoPreview.vue";
    import cancelSignaturesDialog from "./cancelSignaturesDialog.vue";

    export default {
        components: {
            resendContactInfoPreview,
            cancelSignaturesDialog
        },
        props: {
            showCancelIncomplete: { type: Boolean, required: false, default: false }
        },
        emits: ["resend-notifications", "cancel-incomplete", "cancel-all"],
        data() {
            return {
                showCancelSection: false,
                showResendHelp: false,
                alertSeverity: { ...ALERT_SEVERITY },
                cancelExplanationOptions: [
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.ResendingNotification,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.CorrectingRecipientContactInfo,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.UpdatingContents,
                    ESIGNATURE_CANCEL_EXPLANATION_TYPE.Other
                ]
            };
        },
        computed: {
            documentStatuses() {
                return this.classicESignatureDocumentStatuses;
            },
            documentsToResend() {
                return this.documentStatuses.filter((ds) => ds.CanBeResent);
            },
            canResendAnyDocuments() {
                return this.documentStatuses.some((status) => status.CanBeResent);
            },
            resendEnabled() {
                return this.canResendAnyDocuments && this.allResendableSignatoriesHaveContactInfo;
            },
            // True if there's contact info present for all the existing signatories.
            // Could be false if the field used for one of their pieces of contact info has been cleared
            // since the envelope was first sent, in which case the envelope can't be resent
            allResendableSignatoriesHaveContactInfo() {
                for (const document of this.documentsToResend) {
                    const resendableSignatories = this.getResendableSignatories(document);
                    if (resendableSignatories.some((sig) => !this.isSignatoryCurrentContactInfoComplete(sig))) {
                        return false;
                    }
                }
                return true;
            },
            ...mapI18n({ ...KEYS }, NAMESPACE),
            ...Vuex.mapState(PROJECT_FORM_NAMESPACE, {
                hasResent: (state) => state.hasResentESignatures
            }),
            ...Vuex.mapGetters({
                getProjectDataValue: PROJECT_TYPES.getters.dataFieldValueFromId,
                classicESignatureDocumentStatuses: PROJECT_FORM_TYPES.getters.classicESignatureDocumentStatuses
            })
        },
        mounted() {
            // Check expected stores present
            if (!isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state)) {
                throw "Expected ProjectFormStore to be registered";
            }
            if (!isModuleRegistered(PROJECT_NAMESPACE, this.$store.state)) {
                throw "Expected ProjectStore to be registered";
            }
        },
        methods: {
            getResendableSignatories(documentStatus) {
                return documentStatus.Signatories.filter((s) => !s.IsReady);
            },
            isSignatoryCurrentContactInfoComplete(signatory) {
                const sigDef = signatory.SignatoryDefinition;
                let hasAll = true;
                hasAll &= Boolean(this.getProjectDataValue(sigDef.FirstNameDataFieldId));
                hasAll &= Boolean(this.getProjectDataValue(sigDef.LastNameDataFieldId));
                hasAll &= Boolean(this.getProjectDataValue(sigDef.EmailDataFieldId));
                return hasAll;
            }
        }
    };
</script>
